import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { showNotification, useQueryFetch } from 'sonata-react-components';
import { getLineItem } from 'sonata-static-storage/lib/entities';

import { GlobalCatch } from '@/components/common/GlobalCatch';
import FormProviderWrapperLineItem from '@/components/ioSettings/InsertionOrderForm/FormProviderWrapper/FormProviderWrapperLineItem';
import { Skeleton } from '@/components/ioSettings/Skeleton';
import { useDoohAudience } from '@/hooks/useDoohAudience';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { createStore, Provider } from '@/store/local/lineItem';
import { LIWizardLayout } from '../common/entityLayout';

export const Catch = () => {
  return <GlobalCatch />;
};

export default function _layout() {
  const { formatMessage } = useIntl();
  const { pathname, state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const userEntity = useGlobalStore(getUser);
  const isCreate = pathname.includes('create');
  const isValidCreate = isCreate && Boolean(state?.insertionOrder);
  const isEdit = pathname.includes('edit');
  const isValidEdit = isEdit && Boolean(id);

  useEffect(() => {
    if ((isCreate && !isValidCreate) || (isEdit && !isValidEdit) || (!isCreate && !isEdit)) {
      showNotification(
        'warning',
        formatMessage({
          defaultMessage: 'Looks like this line item page is no longer available. Taking you back to your line items…',
          id: 'lineItems.errorInvalidAccess'
        })
      );
      navigate('/');
    }
  }, []);

  const {
    data: lineItem,
    errorMsg: lineItemErrorMsg,
    isFetching: lineItemIsFetching
  } = useQueryFetch(['ioSettings_lineItem'], () => getLineItem(id!), {
    enabled: isValidEdit,
    staleTime: 0
  });

  // A new line item starts with the audience of its insertion order, which it inherits on creation
  const { doohAudience, isFetching: doohAudienceIsFetching } = useDoohAudience(
    isValidEdit ? 'lineItem' : 'insertionOrder',
    isValidEdit ? id : state?.insertionOrder?._id,
    isValidEdit ? lineItem?.type : state?.insertionOrder?.type
  );

  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!lineItem && isValidEdit && !lineItemIsFetching && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      if (lineItemErrorMsg === 'Not Found') {
        showNotification(
          'warning',
          formatMessage({
            defaultMessage: 'Looks like this line item no longer exists. Taking you back to your campaign plans…',
            id: 'lineItems.errorNotFound'
          })
        );
      } else {
        showNotification(
          'warning',
          formatMessage({
            defaultMessage:
              'We couldn’t fetch the line item due to a connection or server error. Redirecting to your campaign plans…',
            id: 'lineItems.errorFetch'
          })
        );
      }
      navigate('/');
    }
  }, [lineItem, isValidEdit, lineItemIsFetching, lineItemErrorMsg]);

  if (lineItemIsFetching || doohAudienceIsFetching) {
    return <Skeleton />;
  }

  if (!lineItem && isValidEdit) {
    return null;
  }

  const currentLineItem = isCreate && state?.lineItem ? state.lineItem : isValidEdit && lineItem ? lineItem : undefined;

  return (
    <Provider createStore={() => createStore(currentLineItem, state?.insertionOrder, userEntity, { doohAudience })}>
      <FormProviderWrapperLineItem>
        <LIWizardLayout entityType="lineItem" />
      </FormProviderWrapperLineItem>
    </Provider>
  );
}
